const CONTROL_API_URL =
  import.meta.env.VITE_CONTROL_API_URL ?? "https://control.yesward.io";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${CONTROL_API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
  };
  if (!response.ok) {
    throw new ApiError(response.status, payload.error ?? "request_failed");
  }
  return payload as T;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
  tier: "shadow" | "growth" | "scale";
  subscriptionStatus: string;
  accessSource: "paid" | "beta" | "manual";
  betaEndsAt: number | null;
}

export interface BootstrapResponse {
  user: { id: string; email: string; name: string };
  organizations: OrganizationSummary[];
}

export interface SiteSummary {
  id: string;
  name: string;
  slug: string;
  public_key: string;
  timezone: string;
  currency: string;
  outcome_type: "lead" | "purchase" | "booking" | "custom";
  consent_mode: string;
  status: "draft" | "verifying" | "shadow" | "live" | "paused";
  origin: string | null;
}

export interface LodgifyConnection {
  provider: "lodgify";
  status:
    | "disconnected"
    | "pending_authorization"
    | "connected"
    | "error"
    | "revoked";
  site: {
    id: string;
    name: string;
    environment: "development" | "staging" | "production";
  };
  properties: Array<{ id: number; name: string | null }>;
  events: string[];
  lastVerifiedAt: number | null;
  lastErrorCode: string | null;
}

export function getBootstrap() {
  return requestJson<BootstrapResponse>("/v1/bootstrap");
}

export function getSites(organizationId: string) {
  return requestJson<{ sites: SiteSummary[] }>(
    `/v1/organizations/${organizationId}/sites`,
  );
}

export function signIn(email: string, password: string) {
  return requestJson<{ user: BootstrapResponse["user"] }>(
    "/api/auth/sign-in/email",
    { method: "POST", body: JSON.stringify({ email, password }) },
  );
}

export function signOut() {
  return requestJson<Record<string, never>>("/api/auth/sign-out", {
    method: "POST",
  });
}

function lodgifyPath(
  organizationId: string,
  siteId: string,
  suffix = "",
) {
  return `/v1/organizations/${organizationId}/sites/${siteId}/environments/production/providers/lodgify${suffix}`;
}

export function getLodgifyConnection(
  organizationId: string,
  siteId: string,
) {
  return requestJson<{ connection: LodgifyConnection }>(
    lodgifyPath(organizationId, siteId),
  );
}

export function connectLodgify(
  organizationId: string,
  siteId: string,
  apiKey: string,
) {
  return requestJson<{ connection: LodgifyConnection }>(
    lodgifyPath(organizationId, siteId, "/connect"),
    { method: "POST", body: JSON.stringify({ apiKey }) },
  );
}

export function verifyLodgifyConnection(
  organizationId: string,
  siteId: string,
) {
  return requestJson<{ connection: LodgifyConnection }>(
    lodgifyPath(organizationId, siteId, "/verify"),
    { method: "POST" },
  );
}

export function disconnectLodgify(
  organizationId: string,
  siteId: string,
) {
  return requestJson<{ connection: LodgifyConnection }>(
    lodgifyPath(organizationId, siteId),
    { method: "DELETE" },
  );
}
